import { useLogger } from "../../../../contexts/LoggerContext";
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  timeToSeconds,
  secondsToTime,
  MAX_TIME,
  MIN_TIME,
} from "../../../../helpers/helpers";

export const GameClockInput: React.FC = () => {
  const { gameSituation, setGameSituation } = useLogger();
  const [minutes, setMinutes] = useState<string>("");
  const [seconds, setSeconds] = useState<string>("");

  // Update local state when gameSituation changes externally
  useEffect(() => {
    const [mm, ss] = gameSituation.time_remaining.split(":");
    setMinutes(mm);
    setSeconds(ss);
  }, [gameSituation.time_remaining]);

  const updateGameClock = (newMinutes: string, newSeconds: string) => {
    const mm = Math.min(Math.max(parseInt(newMinutes) || 0, 0), 12);
    const ss = Math.min(Math.max(parseInt(newSeconds) || 0, 0), 59);
    
    const totalSeconds = mm * 60 + ss;
    const maxSeconds = timeToSeconds(MAX_TIME.format("mm:ss"));
    const minSeconds = timeToSeconds(MIN_TIME.format("mm:ss"));
    
    const clampedSeconds = Math.min(Math.max(totalSeconds, minSeconds), maxSeconds);
    const newTime = secondsToTime(clampedSeconds);
    
    const [newMM, newSS] = newTime.split(":");
    setMinutes(newMM);
    setSeconds(newSS);
    
    setGameSituation({
      ...gameSituation,
      time_remaining: newTime,
    });
  };

  const handleMinuteChange = (value: string) => {
    if (/^\d*$/.test(value) && value.length <= 2) {
      updateGameClock(value, seconds);
    }
  };

  const handleSecondChange = (value: string) => {
    if (/^\d*$/.test(value) && value.length <= 2) {
      updateGameClock(minutes, value);
    }
  };

  const adjustMinutes = (delta: number) => {
    const currentMinutes = parseInt(minutes) || 0;
    updateGameClock((currentMinutes + delta).toString(), seconds);
  };

  const adjustSeconds = (delta: number) => {
    const currentSeconds = parseInt(seconds) || 0;
    let newSeconds = currentSeconds + delta;
    let minutesDelta = 0;

    if (newSeconds >= 60) {
      minutesDelta = 1;
      newSeconds -= 60;
    } else if (newSeconds < 0) {
      minutesDelta = -1;
      newSeconds += 60;
    }

    if (minutesDelta !== 0) {
      const newMinutes = (parseInt(minutes) || 0) + minutesDelta;
      updateGameClock(newMinutes.toString(), newSeconds.toString());
    } else {
      updateGameClock(minutes, newSeconds.toString());
    }
  };

  const handleQuickSubtract = (secondsToSubtract: number) => {
    const currentSeconds = timeToSeconds(gameSituation.time_remaining);
    const newSeconds = Math.max(0, currentSeconds - secondsToSubtract);
    const newTime = secondsToTime(newSeconds);
    const [newMM, newSS] = newTime.split(":");
    updateGameClock(newMM, newSS);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        p: 2,
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ marginBottom: 2 }}>
        Game Clock
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2,
        }}
      >
        <TextField
          label="Minutes"
          value={minutes}
          onChange={(e) => handleMinuteChange(e.target.value)}
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            style: { textAlign: "center", width: "3ch" },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <IconButton
                    size="small"
                    onClick={() => adjustMinutes(1)}
                    sx={{ p: 0 }}
                  >
                    <KeyboardArrowUpIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => adjustMinutes(-1)}
                    sx={{ p: 0 }}
                  >
                    <KeyboardArrowDownIcon />
                  </IconButton>
                </Box>
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="h5">:</Typography>
        <TextField
          label="Seconds"
          value={seconds}
          onChange={(e) => handleSecondChange(e.target.value)}
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            style: { textAlign: "center", width: "3ch" },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <IconButton
                    size="small"
                    onClick={() => adjustSeconds(1)}
                    sx={{ p: 0 }}
                  >
                    <KeyboardArrowUpIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => adjustSeconds(-1)}
                    sx={{ p: 0 }}
                  >
                    <KeyboardArrowDownIcon />
                  </IconButton>
                </Box>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Button
          variant="outlined"
          size="large"
          onClick={() => handleQuickSubtract(15)}
        >
          -15s
        </Button>
        <Button
          variant="outlined"
          size="large"
          onClick={() => handleQuickSubtract(30)}
        >
          -30s
        </Button>
      </Box>
    </Box>
  );
};